import { all, call, select, put, takeEvery, delay } from 'redux-saga/effects';
import { format, addYears } from 'date-fns';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';

import {
  DashboardUserMessage,
  VenueMessageType,
} from '@classpass/protobuf-definitions/lib/web/models/partners/message';
import {
  CancelReservationRequest,
  ReservationStatus,
} from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';

import { FULL_ISO_DATE_FORMAT, EMAIL_TYPES, SERVER_DATE_FORMAT } from 'constants/logic';
import URLS from 'constants/urls';
import { calendarAvailability, practitioners, venueInstructors, venueDetails } from 'store/saga/entities/bound';
import { selectActiveVenueId, selectActiveVenue, selectUserId, selectVenueTimezone } from 'selectors/general';
import { reportError } from 'helpers/error-logging';
import { trackAppointmentCancellation } from 'helpers/events';
import { transformKeysToSnakeCase } from 'helpers/core';
import { adjustDateForTimezone } from 'helpers/intl-fns';
import { makePath } from 'helpers/link-generation';
import { ACTIVE_VENUE_CHANGED } from 'features/App/App.saga';
import {
  UPDATE_RESERVATION_ATTENDANCE,
  doUpdateReservationAttendance,
  doEmailScheduleAttendees,
} from 'features/YieldManagement/YieldManagement.saga';
import { selectPageIsVisible } from 'selectors/ui';
import { setKeyForUser } from 'helpers/local-storage';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { waitForModal, closeModal, openModal } from 'components/Modal';

import { sagaMessages as messages } from './Appointments.messages';
import { selectAvailabilityCalendarDates } from './AvailabilityCalendar/AvailabilityCalendar.selectors';
import {
  setAppointments,
  setUpdates,
  resetAppointmentsState,
  getAppointmentsLSKey,
  addDetachedService,
} from './reducer';
import { selectCachedAppointments, selectAppointments } from './selectors';
import {
  PARTNER_CANCEL_REASON,
  TIME_BLOCK_ELIGIBLE_REASONS,
  ALTERNATIVE_TIME_ELIGIBLE_REASONS,
  PARTNER_CANCEL_REASON_MESSAGES,
} from './CancelAppointment/CancelAppointment.helpers';
import { transformAlternativeTimeToAPIShape } from './CancelAppointment/AlternativeTime/AlternativeTime.helpers';

export const EMAIL_MEMBER = 'EMAIL_MEMBER';
export const emailMember = (values, formAsyncFns) => ({
  type: EMAIL_MEMBER,
  ...values,
  ...formAsyncFns,
});

export const CANCEL_APPOINTMENT = 'CANCEL_APPOINTMENT';
export const cancelAppointment = (formValues, formAsyncFns) => ({
  type: CANCEL_APPOINTMENT,
  formValues,
  formAsyncFns,
});

export const CHANGE_START_DATE = 'CHANGE_START_DATE';
export const changeCalendarDates = startDate => ({
  type: CHANGE_START_DATE,
  startDate,
});

const DETACH_SERVICE = 'APPOINTMENTS/DETACH_SERVICE';
export const detachService = externalClassId => ({
  type: DETACH_SERVICE,
  externalClassId,
});

export const REFRESH_APPOINTMENTS_AFTER_RESERVATION_FEEDBACK = 'APPOINTMENTS/REFRESH_AFTER_RESERVATION_FEEDBACK';
export const refreshAppointmentsAfterReservationFeedback = ({ venueId, scheduleId } = {}) => ({
  type: REFRESH_APPOINTMENTS_AFTER_RESERVATION_FEEDBACK,
  venueId,
  scheduleId,
});

const ONE_MINUTE = 1000 * 60;
const FIVE_MINUTES = ONE_MINUTE * 5;

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  const activeVenueId = yield select(selectActiveVenueId);

  if (venueId !== activeVenueId) {
    yield put(resetAppointmentsState());
    yield call(client.navigate, makePath.appointments(venueId));
  }
}

export function* fetchAppointmentsData(dependencies) {
  const venueId = yield select(selectActiveVenueId);
  const venueTimezone = yield select(selectVenueTimezone);

  const now = new Date();
  const nowInVenueTimezone = adjustDateForTimezone(now, venueTimezone);
  const startDate = format(nowInVenueTimezone, SERVER_DATE_FORMAT);
  const endDate = format(addYears(nowInVenueTimezone, 1), SERVER_DATE_FORMAT);

  const appointmentsData = yield call(fetchEntity, calendarAvailability, [dependencies, venueId, startDate, endDate]);
  return appointmentsData;
}

export function* fetchAndCacheAppointmentsData(dependencies, initial = false) {
  const userId = yield select(selectUserId);
  const venueId = yield select(selectActiveVenueId);
  const { reservations } = yield call(fetchAppointmentsData, dependencies);

  if (initial) {
    yield put(setAppointments(reservations));
  } else {
    yield put(setUpdates(reservations));
    yield call(setKeyForUser, userId, getAppointmentsLSKey({ venueId }), reservations);
  }
}

export function* updateReservationAttendance(dependencies, action) {
  yield call(doUpdateReservationAttendance, dependencies, action);
  yield call(fetchAndCacheAppointmentsData, dependencies);
}

export function* pollForAppointmentsUpdates(dependencies, initial) {
  const venueId = yield select(selectActiveVenueId);
  yield all([
    call(fetchEntity, venueInstructors, [dependencies, venueId]),
    call(fetchAndCacheAppointmentsData, dependencies, initial),
  ]);

  const isPageVisible = yield select(selectPageIsVisible);
  const timeout = isPageVisible ? ONE_MINUTE : FIVE_MINUTES;
  yield delay(timeout);
  yield call(pollForAppointmentsUpdates, dependencies);
}

export function* handleRouteEnter(dependencies) {
  const cachedAppointments = yield select(selectCachedAppointments);

  if (cachedAppointments) {
    yield put(setAppointments(cachedAppointments));
  }

  const { isMsInventoryVenue, venueId } = yield select(selectActiveVenue);

  if (isMsInventoryVenue) {
    yield call(fetchEntity, practitioners, [dependencies, venueId]);
  }

  yield call(fetchEntityIfNeeded, venueDetails, [dependencies, venueId]);
  yield call(pollForAppointmentsUpdates, dependencies, !cachedAppointments);
}

export function* doEmailMember(dependencies, action) {
  const {
    schedule: { reservationId },
  } = action;

  yield call(fetchAndCacheAppointmentsData, dependencies);

  const reservations = yield select(selectAppointments);
  const { reservationStatus } = reservations.find(({ reservationId: rId }) => rId === reservationId) || {};

  if (reservationStatus === ReservationStatus.CANCELLED) {
    const { rejectSubmit } = action;
    yield all([
      put(createErrorBanner(messages.error.userHasCancelledAppointment)),
      call(rejectSubmit),
      put(closeModal()),
    ]);
    return;
  }

  yield call(doEmailScheduleAttendees, dependencies, action);
}

function* createStaffBlock(dependencies, action) {
  const { httpClient, intl } = dependencies;
  const { formValues } = action;
  const { cancelReason, endDateTime, startDateTime, teacherName, teacherOpenapiId, venueId } = formValues;

  const staffBlockEndpoint = URLS.blockOffTime(venueId);
  const staffBlockPayload = transformKeysToSnakeCase({
    endDatetime: format(endDateTime, FULL_ISO_DATE_FORMAT),
    externalItemId: teacherOpenapiId,
    itemType: 'practitioner',
    startDatetime: format(startDateTime, FULL_ISO_DATE_FORMAT),
    description:
      typeof cancelReason === 'object'
        ? intl.formatMessage(PARTNER_CANCEL_REASON_MESSAGES[cancelReason], { teacherName })
        : '',
  });

  yield call([httpClient, httpClient.post], staffBlockEndpoint, staffBlockPayload);
}

export function* cancelNonAlternativeTimeAppointment(dependencies, action) {
  const { httpClient } = dependencies;
  const { formValues } = action;
  const { cancelReason, isMsInventoryVenue, reservationId, clientMessage } = formValues;

  const cancelEndpoint = URLS.venue.cancelReservation;

  const dashboardUserMessage = DashboardUserMessage.create({
    messageType: VenueMessageType.CANCEL,
    messageReason: cancelReason,
    messageContent: clientMessage,
    internalMessage: '',
    subject: '',
  });

  const cancelReservationRequest = CancelReservationRequest.create({
    reservation_id: reservationId,
    message: dashboardUserMessage,
  });

  yield call([httpClient, httpClient.post], cancelEndpoint, cancelReservationRequest);
  yield put(createInfoBanner(messages.success.appointmentCancelled));

  if (TIME_BLOCK_ELIGIBLE_REASONS.includes(cancelReason) && isMsInventoryVenue) {
    yield call(createStaffBlock, dependencies, action);
  }
}

export function* doCancelAppointment(dependencies, action) {
  const { httpClient } = dependencies;
  const { formValues, formAsyncFns } = action;
  const { resolveSubmit } = formAsyncFns;
  const {
    blockSelectedDays,
    cancelReason,
    cancelReasonDescription,
    endDateTime,
    duration,
    isAlternativeTimesEligibleBookableType,
    isMsInventoryVenue,
    message,
    reservationId,
    schedule,
    startTimes,
    teacherName,
  } = formValues;
  const { startDateTime } = schedule;
  const venueId = yield select(selectActiveVenueId);

  const clientMessage = message.trim() ? message.replace(/\n/g, '<br/>') : '';
  const cancelMessagePayload = transformKeysToSnakeCase({
    message: {
      messageType: EMAIL_TYPES.CANCEL,
      messageReason: cancelReason,
      messageContent: clientMessage,
      subject: '',
      messageTemplateVars: {},
    },
  });

  if (
    !ALTERNATIVE_TIME_ELIGIBLE_REASONS.includes(cancelReason) ||
    !isAlternativeTimesEligibleBookableType ||
    !isMsInventoryVenue
  ) {
    yield call(cancelNonAlternativeTimeAppointment, dependencies, {
      formValues: {
        ...formValues,
        startDateTime,
        venueId,
        reservationId,
        clientMessage,
      },
    });
  } else {
    const confirmed = yield call(waitForModal, dependencies, 'AlternativeTimeConfirmationModal', {
      appointmentTime: {
        endDateTime,
        startDateTime,
      },
      clientMessage,
      payload: { blockSelectedDays, startTimes },
      teacherName,
    });

    if (!confirmed) {
      yield call(resolveSubmit);
      return;
    }

    yield put(openModal('AlternativeTimeLoadingModal'));

    const alternativeTimes = startTimes?.reduce((acc, { times }) => acc.concat(times), []);
    const hasAlternativeTimesData = alternativeTimes?.length > 0 || blockSelectedDays;

    // The suggest alternate times endpoint requires a list of times or day blocks. If we're not suggesting times
    // or blocking full days, we can just cancel the appointment and create a staff block for the appointment time separately.
    if (!hasAlternativeTimesData) {
      yield call(cancelNonAlternativeTimeAppointment, dependencies, {
        formValues: {
          ...formValues,
          startDateTime,
          venueId,
          reservationId,
          clientMessage,
        },
      });
    } else {
      const appointmentAlternativeTimeEndpoint = URLS.venue.appointmentAlternativeTime(venueId, reservationId);
      const alternativeTimePayload = {
        ...transformAlternativeTimeToAPIShape({ blockSelectedDays, startTimes }, duration),
        ...cancelMessagePayload,
      };

      try {
        yield call([httpClient, httpClient.post], appointmentAlternativeTimeEndpoint, alternativeTimePayload);
        yield call(resolveSubmit);
        yield put(createInfoBanner(messages.success.appointmentAlternativeTimeSaved));
      } catch (error) {
        yield put(createErrorBanner(messages.error.cannotCancelReservation));
      }
    }

    yield put(closeModal());
  }

  yield call(fetchAndCacheAppointmentsData, dependencies);
  yield call(trackAppointmentCancellation, { cancelReason, cancelReasonDescription });

  if (cancelReason === PARTNER_CANCEL_REASON.LOCATION_CLOSED) {
    const isAddVenueBlockConfirmed = yield call(waitForModal, dependencies, 'AppointmentAddVenueBlockModal');

    if (isAddVenueBlockConfirmed) {
      yield call(dependencies.navigate, makePath.editVenue(venueId, 'settings', 'disabledTimeWindows'));
      return;
    }
  }

  yield call(resolveSubmit);
  yield call(dependencies.navigate, -1);
}

function* refreshAppointmentsCacheAfterReservationFeedback(dependencies) {
  try {
    yield call(fetchAndCacheAppointmentsData, dependencies);
    const venueId = yield select(selectActiveVenueId);
    const { startDate, endDate } = yield select(selectAvailabilityCalendarDates);
    if (startDate != null && endDate != null) {
      yield call(fetchEntity, calendarAvailability, [dependencies, venueId, startDate, endDate]);
    }
  } catch (error) {
    yield call(reportError, error);
  }
}

export function* doDetachService(dependencies, action) {
  const { httpClient } = dependencies;
  const { externalClassId } = action;
  const venueId = yield select(selectActiveVenueId);

  const endpoint = URLS.msInventory.detachService(venueId, externalClassId);

  try {
    yield call([httpClient, httpClient.post], endpoint);
    yield put(addDetachedService(externalClassId));
    yield put(createInfoBanner(messages.success.serviceDetached));
  } catch (error) {
    yield put(createErrorBanner(messages.error.cannotDetachService));
  }
}

export function* watchActions(dependencies) {
  yield takeEvery(EMAIL_MEMBER, doEmailMember, dependencies);
  yield takeEvery(UPDATE_RESERVATION_ATTENDANCE, updateReservationAttendance, dependencies);
  yield takeEvery(CANCEL_APPOINTMENT, doCancelAppointment, dependencies);
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, dependencies);
  yield takeEvery(DETACH_SERVICE, doDetachService, dependencies);
  yield takeEvery(
    REFRESH_APPOINTMENTS_AFTER_RESERVATION_FEEDBACK,
    refreshAppointmentsCacheAfterReservationFeedback,
    dependencies
  );
}
